import React, { useState, useEffect, useMemo } from "react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";

interface SpeciesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
}

export const SpeciesAutocomplete: React.FC<SpeciesAutocompleteProps> = ({
  value,
  onChange,
  suggestions,
  placeholder = "e.g., Fennec Fox, Capybara, Tiger",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  
  // Debounce input for filtering (300ms) - prevents rapid re-renders and popover flashing
  const debouncedInput = useDebounce(inputValue, 300);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter suggestions - prioritize startsWith matches
  const filteredSuggestions = useMemo(() => {
    if (!debouncedInput || debouncedInput.length < 3) return [];
    
    const searchTerm = debouncedInput.toLowerCase();
    
    // Split into starts-with and contains
    const startsWithMatches: string[] = [];
    const containsMatches: string[] = [];
    
    suggestions.forEach((suggestion) => {
      const lowerSuggestion = suggestion.toLowerCase();
      if (lowerSuggestion.startsWith(searchTerm)) {
        startsWithMatches.push(suggestion);
      } else if (lowerSuggestion.includes(searchTerm)) {
        containsMatches.push(suggestion);
      }
    });
    
    // Combine: starts-with first, then contains
    return [...startsWithMatches, ...containsMatches].slice(0, 8);
  }, [debouncedInput, suggestions]);

  // Auto-open popover when suggestions are ready AND input is focused
  useEffect(() => {
    if (isFocused && filteredSuggestions.length > 0 && debouncedInput.length >= 3) {
      setOpen(true);
    }
  }, [filteredSuggestions, isFocused, debouncedInput]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    // Don't auto-open popover here - wait for debounced suggestions
  };

  const handleSelect = (selectedValue: string) => {
    setInputValue(selectedValue);
    onChange(selectedValue);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      e.preventDefault();
    }
    
    // Arrow down opens suggestions if available
    if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
      setOpen(true);
      e.preventDefault();
    }
    
    // Prevent Enter from submitting if popover is closed
    if (e.key === 'Enter' && !open) {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Delay closing to allow click on suggestion to register
    setTimeout(() => setOpen(false), 200);
  };

  return (
    <Popover 
      open={open && filteredSuggestions.length > 0} 
      onOpenChange={setOpen}
      modal={false}
    >
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <Input
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => {
              setIsFocused(true);
              // Open if suggestions already available
              if (inputValue.length >= 3 && filteredSuggestions.length > 0) {
                setOpen(true);
              }
            }}
            className={className}
            autoComplete="off"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 w-[var(--radix-popover-trigger-width)]" 
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Don't close if clicking on the input
          if (e.target instanceof Element && e.target.tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
      >
        <Command shouldFilter={false}>
          <CommandList>
            <CommandEmpty>No species found.</CommandEmpty>
            <CommandGroup>
              {filteredSuggestions.map((suggestion) => (
                <CommandItem
                  key={suggestion}
                  onSelect={() => handleSelect(suggestion)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      inputValue.toLowerCase() === suggestion.toLowerCase()
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {suggestion}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
